import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { TrendPoint } from '../types/adminApiTypes';
import { Box, Typography, Paper } from '@mui/material';
import { formatDate } from '../utils/helpers';

interface UsageChartProps {
  data: TrendPoint[];
  metricLabel: string;
  timeBucket: 'daily' | 'weekly' | 'monthly' | 'quarterly';
}

// Helper to format date ticks based on time bucket
const formatTick = (tickItem: string, timeBucket: 'daily' | 'weekly' | 'monthly' | 'quarterly') => {
  switch (timeBucket) {
    case 'quarterly':
      return formatDate(tickItem, '[Q]Q YYYY'); // Show quarter and year (e.g., Q1 2025)
    case 'monthly':
      return formatDate(tickItem, 'MMM YYYY');
    case 'weekly':
      return formatDate(tickItem, 'D MMM YYYY'); // Show start of week with year
    case 'daily':
    default:
      return formatDate(tickItem, 'D MMM YYYY'); // Show day with year
  }
};

const UsageChart: React.FC<UsageChartProps> = ({ data, metricLabel, timeBucket }) => {
  if (!data || data.length === 0) {
    return <Typography>No trend data available for the selected period.</Typography>;
  }

  // Prepare data for Recharts (ensure timestamp is suitable for axis)
  const chartData = data.map(point => ({
    ...point,
    // Optional: If timestamp isn't directly usable, parse/format it here
    // timestamp: new Date(point.timestamp).getTime() // Example if needed
  }));

  return (
    <Paper sx={{ p: 2, height: 350, width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="timestamp"
            tickFormatter={tick => formatTick(tick, timeBucket)}
            // Adjust interval if ticks are too crowded
            // interval="preserveStartEnd"
            angle={-30} // Angle ticks for better readability if needed
            textAnchor="end"
            height={50} // Increase height to accommodate angled ticks
          />
          <YAxis allowDecimals={false} />
          <Tooltip
            labelFormatter={label => formatTick(label, timeBucket)}
            formatter={(value: number) => [value, metricLabel]}
          />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />
          <Line
            type="monotone"
            dataKey="count"
            name={metricLabel} // Use name for Legend
            stroke="#8884d8"
            activeDot={{ r: 8 }}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default UsageChart;
